import React, { useEffect, useState } from 'react';
import { Mail, Package, Phone, User } from 'lucide-react';
import { AssignDriverModal } from './AssignDriverModal';
import { ContactActionModal, buildOrderContact } from './ContactActionModal';
import { StatusBadge } from './StatusBadge';
import { TERMINAL_STATUSES } from './restaurantDeliveryConfig';
import {
  calcRemainingTime,
  formatTimeLeftCardLabel,
  getCardStatusDisplay,
  getOrderCardRouteFillPercent,
  getOrderCardRouteSummary,
  getTimeLeftUrgency,
  isOrderDelivered,
} from './restaurantDeliveryOrderHelpers';

function OrderCardTimeLeft({ rawOrder }) {
  const [now, setNow] = useState(() => Date.now());
  const status = String(rawOrder?.internal_order_status ?? '');
  const isTerminal = TERMINAL_STATUSES.has(status);

  useEffect(() => {
    if (isTerminal) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isTerminal, rawOrder?.delivery_date, rawOrder?.delivery_time_slot]);

  const rem = calcRemainingTime(rawOrder, now);
  if (!rem) return null;

  const urgency = getTimeLeftUrgency(rem.totalSeconds);
  const label = formatTimeLeftCardLabel(rem.totalSeconds);

  return (
    <>
      <span className='otd-order-card__timing-sep'> — </span>
      <span className='otd-order-card__timing-label'>Time Left</span>{' '}
      <span
        className={`otd-order-card__timing-value otd-order-card__timing-value--time-left-${urgency}`}
      >
        {label}
      </span>
    </>
  );
}

function shortenAddress(address, maxLen = 42) {
  if (!address) return '—';
  const text = String(address).trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1)}…`;
}

function OrderCardRouteTrack({ hasDriver, pickedUp, progress, milesLabel, rawOrder }) {
  const fillPct = getOrderCardRouteFillPercent({
    hasDriver,
    pickedUp,
    progress,
    rawOrder,
  });
  const delivered = rawOrder ? isOrderDelivered(rawOrder) : false;

  return (
    <div className='otd-order-card__track'>
      <div className='otd-order-card__track-line' aria-hidden>
        <span
          className='otd-order-card__track-fill'
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <span
        className='otd-order-card__track-node otd-order-card__track-node--start otd-order-card__track-node--active'
        aria-hidden
      />
      {milesLabel ? (
        <span className='otd-order-card__track-distance'>{milesLabel}</span>
      ) : null}
      <span
        className={`otd-order-card__track-node otd-order-card__track-node--end ${
          delivered || fillPct >= 100 ? 'otd-order-card__track-node--active' : ''
        }`}
        aria-hidden
      />
    </div>
  );
}

export function OrderCard({ order, selected, onSelect }) {
  const rawOrder = order.raw;
  const driver = rawOrder?.driver_information ?? {};
  const hasDriver = order.hasDriver;

  const [contactModal, setContactModal] = useState({ open: false, type: 'call', contact: null });
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);

  const cardStatus = getCardStatusDisplay(rawOrder?.internal_order_status);
  const { milesLabel } = getOrderCardRouteSummary(rawOrder, order.routeMode);
  const deliveryBefore = order.routeTime ?? '--';
  const pickedUp = order.pickedUp;

  const openContactModal = (type) => {
    setContactModal({
      open: true,
      type,
      contact: buildOrderContact(rawOrder, 'driver', driver),
    });
  };

  const closeContactModal = () => setContactModal((prev) => ({ ...prev, open: false }));

  const handleContactSubmit = async (payload) => {
    if (payload.type === 'call' && payload.contact?.phone) {
      const digits = String(payload.contact.phone).replace(/[^\d+]/g, '');
      if (digits) window.open(`tel:${digits}`, '_self');
    }
    await new Promise((resolve) => setTimeout(resolve, 400));
  };

  const stopCardClick = (event) => event.stopPropagation();

  const handleDriverAction = (event) => {
    stopCardClick(event);
    setAssignDriverOpen(true);
  };

  return (
    <div className='otd-order-card-wrap'>
      <div
        role='button'
        tabIndex={0}
        className={`otd-order-card ${selected ? 'otd-order-card--selected' : ''}`}
        onClick={() => onSelect(order.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(order.id);
          }
        }}
      >
        <header className='otd-order-card__header'>
          <div className='otd-order-card__id-row'>
            <span className='otd-order-card__icon-box' aria-hidden>
              <Package size={14} />
            </span>
            <span className='otd-order-card__id'>#{order.orderNo}</span>
          </div>
          <StatusBadge
            status={rawOrder?.internal_order_status}
            label={cardStatus.label}
            bg={cardStatus.bg}
            color={cardStatus.color}
          />
        </header>

        <section
          className='otd-order-card__body'
          aria-label={order.isPickupOrder ? 'Pickup order' : 'Delivery details'}
        >
          {order.isPickupOrder ? (
            <p className='otd-order-card__pickup-label'>Pickup order</p>
          ) : (
            <>
              <p className='otd-order-card__timing'>
                <span className='otd-order-card__timing-label'>Delivery Before</span>{' '}
                <span className='otd-order-card__timing-value otd-order-card__timing-value--delivery-before'>
                  {deliveryBefore}
                </span>
                <OrderCardTimeLeft rawOrder={rawOrder} />
              </p>

              <OrderCardRouteTrack
                hasDriver={hasDriver}
                pickedUp={pickedUp}
                progress={order.progress}
                milesLabel={milesLabel}
                rawOrder={rawOrder}
              />

              <div className='otd-order-card__addresses'>
                <div className='otd-order-card__address otd-order-card__address--store'>
                  <p className='otd-order-card__address-title'>Own Store</p>
                  <p className='otd-order-card__address-line'>
                    {shortenAddress(order.kitchenAddress || order.restaurantName)}
                  </p>
                </div>
                <div className='otd-order-card__address otd-order-card__address--customer'>
                  <p className='otd-order-card__address-title'>Customer</p>
                  <p className='otd-order-card__address-line'>
                    {shortenAddress(order.deliveryAddress)}
                  </p>
                </div>
              </div>
            </>
          )}
        </section>

        <footer className='otd-order-card__footer'>
          {hasDriver ? (
            <>
              <div className='otd-order-card__driver'>
                <div className='otd-order-card__driver-avatar'>
                  {order.driverImage ? (
                    <img src={order.driverImage} alt='' />
                  ) : (
                    <User size={18} aria-hidden />
                  )}
                </div>
                <div className='otd-order-card__driver-text'>
                  <p className='otd-order-card__driver-name'>{order.driverName}</p>
                  <p className='otd-order-card__driver-role'>Driver</p>
                </div>
              </div>
              <div className='otd-order-card__footer-actions'>
                <button
                  type='button'
                  className='otd-order-card__contact-btn'
                  aria-label='Message driver'
                  onClick={(e) => {
                    stopCardClick(e);
                    openContactModal('sms');
                  }}
                >
                  <Mail size={14} />
                </button>
                <button
                  type='button'
                  className='otd-order-card__contact-btn'
                  aria-label='Call driver'
                  onClick={(e) => {
                    stopCardClick(e);
                    openContactModal('call');
                  }}
                >
                  <Phone size={14} />
                </button>
                <button
                  type='button'
                  className='otd-order-card__action-btn'
                  onClick={handleDriverAction}
                >
                  Re-Assign Driver
                </button>
              </div>
            </>
          ) : (
            <>
              <p className='otd-order-card__unassigned'>Driver Not Assigned</p>
              <button
                type='button'
                className='otd-order-card__action-btn'
                onClick={handleDriverAction}
              >
                Assign Driver
              </button>
            </>
          )}
        </footer>
      </div>

      <ContactActionModal
        open={contactModal.open}
        type={contactModal.type}
        contact={contactModal.contact}
        onClose={closeContactModal}
        onSubmit={handleContactSubmit}
      />

      <AssignDriverModal
        open={assignDriverOpen}
        orderNo={order.orderNo}
        reassign={hasDriver}
        onClose={() => setAssignDriverOpen(false)}
      />
    </div>
  );
}
